import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@carepass/api-client";
import { ChoiceControl, StatusBadge } from "@carepass/ui";
import { memberApi } from "../shared/api";
import type { Transaction } from "../shared/types";
import { MemberActionBar } from "../shared/MemberLayouts";
import { Alert, Button, ErrorState, Field, LoadingState, dateTime, navigate, statusLabel, statusVariant } from "../shared/ui";
import { TransactionSettlementStepHelp, TransactionSettlementSteps } from "./TransactionSettlementSteps";
import { MemberTopBar } from "./MemberMobileUi";
import { TransactionSettlementSummary } from "./TransactionSettlementSummary";
import { isTransactionPaid, settlementError, useSettlementTransaction } from "./transaction-settlement";
import { beginTransferRequest, clearTransferDraft, minimumTransferDate, readTransferDraft, saveTransferDraft, transferDateError, type TransferDraft, type TransferRequestBody } from "./transaction-transfer-request";
import { transactionPath } from "./transaction-groups";
import { cacheTransactionResult } from "./transaction-cache";
import { allowedTransactionAction, transactionActionBlockedMessage } from "./transaction-actions";
import { TransactionActions } from "./TransactionActions";

function TransferSelectionForm({ transaction, refresh, onAcceptedError }: {
  readonly transaction: Transaction;
  readonly refresh: () => Promise<Transaction | undefined>;
  readonly onAcceptedError: (message: string) => void;
}) {
  const client = useQueryClient();
  const [draft, setDraft] = useState(() => readTransferDraft(transaction.memberId, transaction.id));
  const [error, setError] = useState<string>();
  const [checking, setChecking] = useState(false);
  const submitting = useRef(false);
  const dateError = draft.scheduleType === "SCHEDULED" ? transferDateError(draft.date) : undefined;
  const save = (next: TransferDraft) => setDraft(saveTransferDraft(transaction.memberId, transaction.id, next));
  const transfer = useMutation({
    mutationFn: (request: NonNullable<TransferDraft["request"]>) => memberApi.selectTransfer(transaction.id, request.body, request.key),
    onSuccess: result => {
      cacheTransactionResult(client, result);
      if (result.status !== "TRANSFER_SELECTION_PENDING") clearTransferDraft(transaction.memberId, transaction.id);
      setError(undefined);
    },
    onError: async cause => {
      const latest = await refresh();
      if (latest && latest.status !== "TRANSFER_SELECTION_PENDING") {
        clearTransferDraft(transaction.memberId, transaction.id);
        if (latest.status === "TRANSFER_READY" && latest.transferScheduleType === "IMMEDIATE") {
          onAcceptedError(settlementError(cause, "즉시 송금 실행 결과를 확인하지 못했어요. 다시 요청하지 마시고 상태를 확인해 주세요."));
        }
        return;
      }
      const rejected = latest && cause instanceof ApiError && cause.status !== null
        && cause.status >= 400 && cause.status < 500 && cause.code !== "TRANSFER_SELECTION_IDEMPOTENCY_CONFLICT";
      if (rejected) {
        save({ scheduleType: draft.scheduleType, date: draft.date });
        setError(settlementError(cause, "송금 정보를 다시 확인해 주세요."));
      } else {
        setError("송금 요청 결과를 확인하지 못했어요. 상태를 먼저 확인해 주세요. 다시 시도하면 같은 요청으로 확인하며 추가 결제는 하지 않아요.");
      }
    },
    onSettled: () => { submitting.current = false; },
  });
  const submit = async () => {
    if (submitting.current || (!draft.request && (!draft.scheduleType || dateError))) return;
    submitting.current = true;
    setChecking(true);
    setError(undefined);
    const latest = await refresh();
    setChecking(false);
    if (!latest) {
      submitting.current = false;
      setError("최신 송금 상태를 확인하지 못했어요. 상태를 확인한 뒤 다시 시도해 주세요.");
      return;
    }
    if (latest.status !== "TRANSFER_SELECTION_PENDING") {
      clearTransferDraft(transaction.memberId, transaction.id);
      submitting.current = false;
      return;
    }
    if (!allowedTransactionAction(latest, "SELECT_TRANSFER")) {
      submitting.current = false;
      setError(transactionActionBlockedMessage(latest, "SELECT_TRANSFER"));
      return;
    }
    const body: TransferRequestBody = draft.request?.body ?? (draft.scheduleType === "SCHEDULED"
      ? { scheduleType: "SCHEDULED", requestedTransferDate: draft.date } : { scheduleType: "IMMEDIATE" });
    if (!draft.request && body.scheduleType === "SCHEDULED" && transferDateError(body.requestedTransferDate ?? "")) {
      submitting.current = false;
      setError(transferDateError(body.requestedTransferDate ?? ""));
      return;
    }
    try {
      const next = beginTransferRequest(transaction.memberId, transaction.id, body);
      setDraft(next);
      transfer.mutate(next.request!);
    } catch (cause) {
      submitting.current = false;
      setError(cause instanceof Error ? cause.message : "송금 요청 상태를 다시 확인해 주세요.");
    }
  };
  const frozen = Boolean(draft.request) || checking || transfer.isPending;
  return <>
    <Alert persistent variant="success" className="settlement-approved-notice" title="카드 결제가 완료되었어요" />
    {error && <Alert persistent variant="danger" title="송금 요청 상태를 확인해 주세요">{error}</Alert>}
    {draft.request && !transfer.isPending && <Alert persistent variant="information" title="앞선 송금 요청을 확인하고 있어요">확인이 끝날 때까지 송금 방식과 날짜를 바꾸지 않습니다.</Alert>}
    <div className="settlement-transfer-form">
      <fieldset className="settlement-choices" disabled={frozen}>
        <legend>언제 송금할까요?</legend>
        <ChoiceControl variant="radio" name="transfer-choice" label="즉시 송금" description="지금 바로 받는 분 계좌로 송금해요"
          checked={draft.scheduleType === "IMMEDIATE"} onChange={() => save({ scheduleType: "IMMEDIATE", date: "" })} />
        <ChoiceControl variant="radio" name="transfer-choice" label="지정일 송금" description="원하는 날짜 오전 10시에 송금해요"
          checked={draft.scheduleType === "SCHEDULED"} onChange={() => save({ scheduleType: "SCHEDULED", date: draft.date })} />
      </fieldset>
      {draft.scheduleType === "SCHEDULED" && <Field label="송금일" type="date" required min={minimumTransferDate()} value={draft.date} disabled={frozen}
        onChange={event => save({ ...draft, date: event.currentTarget.value })} error={draft.date ? dateError : undefined}
        description="선택한 날짜 오전 10시(서울 기준)부터 송금 처리를 시작해요." />}
      <p id="settlement-transfer-help" className="settlement-assistive">{draft.scheduleType === "SCHEDULED" ? "송금일을 선택한 뒤 예약해 주세요." : "송금 방식을 선택한 뒤 요청해 주세요."}</p>
    </div>
    <MemberActionBar><Button size="large" aria-describedby="settlement-transfer-help" disabled={!draft.request && (!draft.scheduleType || Boolean(dateError))} isLoading={checking || transfer.isPending} onClick={() => void submit()}>
      {draft.request ? "같은 송금 요청 다시 확인" : draft.scheduleType === "SCHEDULED" ? "송금 예약하기" : "지금 송금하기"}
    </Button></MemberActionBar>
  </>;
}

function TransferResult({ transaction, refresh, fetching }: { readonly transaction: Transaction; readonly refresh: () => void; readonly fetching: boolean }) {
  const complete = transaction.status === "COMPLETED";
  const scheduled = transaction.status === "TRANSFER_READY" && transaction.transferScheduleType === "SCHEDULED";
  const failed = transaction.status === "TRANSFER_FAILED";
  const unknown = transaction.status === "TRANSFER_UNKNOWN";
  const support = allowedTransactionAction(transaction, "CONTACT_SUPPORT");
  return <section className="settlement-result" data-result={failed ? "failed" : complete || scheduled ? "success" : "pending"} aria-live="polite">
      <span className="settlement-result-mark" data-scheduled={scheduled || undefined} aria-hidden="true"><svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {complete ? <path d="m5 12 4.2 4.2L19 6.5" /> : failed ? <path d="m6 6 12 12M18 6 6 18" /> : <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>}
      </svg></span>
      <h2>{complete ? "거래가 완료되었어요" : scheduled ? "송금이 예약되었어요" : failed ? "송금을 완료하지 못했어요" : unknown ? "송금 결과를 확인하고 있어요" : "송금을 처리하고 있어요"}</h2>
      <p>{complete ? "카드 결제와 계좌 송금이 모두 완료되었어요. 거래 내역에서 처리 결과를 확인할 수 있어요." : scheduled
        ? `${dateTime(transaction.requestedTransferAt)}부터 받는 분 계좌로 송금할 예정이에요.` : failed
          ? "카드결제는 완료되었어요. 다시 결제하지 마시고 1:1 문의로 확인해 주세요."
          : "이미 송금 요청이 접수되었어요. 다시 실행하지 마시고 최신 상태를 확인해 주세요."}</p>
      <div className="settlement-result-status"><StatusBadge variant={scheduled ? "information" : statusVariant(transaction.status)}>
        현재 상태 · {complete ? "거래 완료" : scheduled ? "송금 대기" : statusLabel(transaction.status)}
      </StatusBadge></div>
    {!(complete || scheduled || failed) && <Button variant="quiet" isLoading={fetching} onClick={refresh}>상태 다시 확인</Button>}
    <MemberActionBar><Button size="large" onClick={() => navigate(failed && support?.route ? support.route : transactionPath(complete || scheduled ? "/app/transactions" : `/app/transactions/${transaction.id}`))}>
      {failed && support ? support.label : complete || scheduled ? "거래 내역 보기" : "거래 상세 보기"}
    </Button></MemberActionBar>
    {unknown && <TransactionActions transaction={transaction} codes={["CONTACT_SUPPORT"]} />}
    {(complete || scheduled) && <Button className="settlement-home-link" variant="quiet" onClick={() => navigate("/app")}>홈으로</Button>}
  </section>;
}

export function TransactionTransferPage({ transactionId }: { readonly transactionId: number }) {
  const { query, refresh } = useSettlementTransaction(transactionId);
  const [acceptedError, setAcceptedError] = useState<string>();
  useEffect(() => { setAcceptedError(undefined); }, [transactionId]);
  const transaction = query.data;
  useEffect(() => {
    if (transaction && transaction.status !== "TRANSFER_SELECTION_PENDING") clearTransferDraft(transaction.memberId, transaction.id);
  }, [transaction?.id, transaction?.memberId, transaction?.status]);
  if (query.isPending) return <><MemberTopBar title="송금하기" backTo={transactionPath(`/app/transactions/${transactionId}`)} /><LoadingState label="송금 상태를 확인하고 있습니다." /></>;
  if (!transaction) return <><MemberTopBar title="송금하기" backTo={transactionPath(`/app/transactions/${transactionId}`)} /><ErrorState error={query.error} onRetry={() => void refresh()} /></>;
  const paid = isTransactionPaid(transaction);
  const selectable = transaction.status === "TRANSFER_SELECTION_PENDING" && Boolean(allowedTransactionAction(transaction, "SELECT_TRANSFER"));
  const hasTransfer = ["TRANSFER_READY", "TRANSFER_PROCESSING", "TRANSFER_UNKNOWN", "TRANSFER_FAILED", "COMPLETED"].includes(transaction.status);
  return <div className="member-settlement settlement-transfer">
    <TransactionSettlementSteps transaction={transaction} stage="transfer" />
    <div className="settlement-body">
    <TransactionSettlementSummary transaction={transaction} stage="transfer" />
    {!paid && <TransactionSettlementStepHelp />}
    {query.isError && <Alert persistent variant="warning" title="최신 송금 상태를 확인하지 못했어요">다시 실행하지 마시고 상태를 먼저 확인해 주세요.</Alert>}
    {acceptedError && transaction.status === "TRANSFER_READY" && transaction.transferScheduleType === "IMMEDIATE" && <Alert persistent variant="warning" title="즉시 송금이 아직 완료되지 않았어요">{acceptedError} 상태를 확인하고 있어요. 다시 송금을 요청하지 마세요.</Alert>}
    {paid && selectable ? <TransferSelectionForm key={`${transaction.memberId}:${transaction.id}`} transaction={transaction} refresh={refresh} onAcceptedError={setAcceptedError} />
      : paid && hasTransfer ? <TransferResult transaction={transaction} refresh={() => void refresh()} fetching={query.isFetching} />
        : <><Alert persistent variant="information" title={paid ? "현재 송금할 수 없는 거래예요" : "카드결제가 먼저 필요해요"}>
          {paid ? transactionActionBlockedMessage(transaction, "SELECT_TRANSFER") : "1 결제에서 카드결제와 진행 상태를 확인해 주세요. 결제가 승인되면 송금 방식을 선택할 수 있어요."}
        </Alert><MemberActionBar><Button size="large" onClick={() => navigate(transactionPath(`/app/transactions/${transactionId}/${paid ? "" : "payment"}`.replace(/\/$/, "")))}>
          {paid ? "거래 상세 보기" : "결제 화면으로"}
        </Button></MemberActionBar>{paid && <TransactionActions transaction={transaction} codes={["SELECT_TRANSFER"]} />}</>}
    </div>
  </div>;
}
